import { defineStore } from 'pinia'
import type { FeatureCollection } from 'geojson'
import {
  getCommunityAccount,
  getProjectEvidences,
  getProjects,
  getPublicAccount,
  getPurchases,
  getReceipts,
} from '@/api'
import type {
  ImpactType,
  IProject,
  IProjectEvidence,
  IProjectRequestParams,
  ITransferredImpact,
  LanguageType,
  TopStat,
} from '@/api/interfaces'
import { IMPACT_TYPES } from '@/api/interfaces'
import type {
  BeesComparison,
  CarbonComparison,
  IReceiptTransferred,
  KelpComparison,
  PlasticComparison,
  PublicPageData,
  PublicStatData,
  TreeComparison,
  WaterComparison,
} from '@/stores/interfaces/PublicData'
import { dispatchMessage, EVENT_TYPES } from '@/composables/iframe'
import { inIFrame } from '@/helpers/utils'
import Decimal from 'decimal.js'
import { sumParentScaleEquivalentAmount } from '@/helpers/project'

const aggregateImpactsByType = (
  impacts: PublicPageData['impacts'],
  projects: IProject[],
): Record<ImpactType, number> => {
  if (projects.length === 0 && (impacts ?? []).some((impact) => impact.projectId)) {
    return IMPACT_TYPES.reduce(
      (totals, type) => ({ ...totals, [type]: 0 }),
      {} as Record<ImpactType, number>,
    )
  }

  return IMPACT_TYPES.reduce(
    (totals, type) => ({
      ...totals,
      [type]: new Decimal(sumParentScaleEquivalentAmount(type, impacts ?? [], projects))
        // the carbon impact is presented as kg in the backend, but we display it in tons
        .dividedBy(type === 'carbon' ? 1000 : 1)
        .toNumber(),
    }),
    {} as Record<ImpactType, number>,
  )
}

export const usePublicData = defineStore('publicData', {
  state: (): PublicPageData => ({
    name: '',
    signupDate: '',
    accountType: 'business',
    projectList: [],
    selectedProjectId: null,
    projectEvidenceById: {},
    isProjectEvidenceLoadingById: {},
    // TODO: We should change this data structure to allow for a "unloaded" state
    // https://greenspark-force.monday.com/boards/3658967791/views/83096355/pulses/7088681338
    impacts: undefined,
    comparisons: [],
    receipts: [],
    purchases: [],
    loading: false,
    lang: import.meta.env.VITE_FALLBACK_LOCALE,
  }),
  getters: {
    getPublicStats({ impacts, projectList }: PublicPageData): PublicStatData {
      return {
        transferredImpacts: aggregateImpactsByType(
          (impacts ?? []).filter((i) => i.source === 'transferred'),
          projectList,
        ),
        relationImpacts: aggregateImpactsByType(
          (impacts ?? []).filter((i) => i.source === 'relation'),
          projectList,
        ),
      }
    },
    getTopStats(s: PublicPageData): { [key in TopStat]: number } {
      const aggregatedImpacts = aggregateImpactsByType(s.impacts, s.projectList)

      const date1 = new Date()
      const date2 = s.signupDate ? new Date(s.signupDate) : new Date()

      let difference = (date2.getTime() - date1.getTime()) / 1000
      difference /= 60 * 60 * 24 * 7 * 4

      const monthsEarthPositive = Math.abs(Math.round(difference))
      return { monthsEarthPositive, ...aggregatedImpacts }
    },
    getComparison({ comparisons }: PublicPageData) {
      return <T>(type: ImpactType): T => {
        return comparisons
          .filter((comparison) => type === comparison.type)
          .reduce(
            (acc, { amount, unit, suffix }) => ({
              ...acc,
              [unit]: {
                value:
                  // converting the carbon sequestered amount from kg to tons
                  unit === 'carbonSequestered'
                    ? new Decimal(amount).dividedBy(1000).toNumber()
                    : amount,
                suffix: unit === 'carbonSequestered' ? 't' : suffix,
              },
            }),
            {},
          ) as T
      }
    },
    getComparisons() {
      return (type: ImpactType) => {
        switch (type) {
          case 'carbon':
            return this.getComparison<CarbonComparison>(type)
          case 'plastic':
            return this.getComparison<PlasticComparison>(type)
          case 'trees':
            return this.getComparison<TreeComparison>(type)
          case 'kelp':
            return this.getComparison<KelpComparison>(type)
          case 'water':
            return this.getComparison<WaterComparison>(type)
          case 'bees':
            return this.getComparison<BeesComparison>(type)
          default: {
            const exhaustiveCheck: never = type
            throw new Error(exhaustiveCheck)
          }
        }
      }
    },
    getTransferredImpactInvoices(): IReceiptTransferred[] {
      const transferredImpactInvoices: IReceiptTransferred[] = []
      for (const { type, urls } of (this.impacts ?? []).filter(
        (i) => i.source === 'transferred',
      ) as ITransferredImpact[]) {
        for (const url of urls) {
          transferredImpactInvoices.push({ type, url })
        }
      }
      return transferredImpactInvoices
    },
    isBusinessAccount(): boolean {
      return ['business', 'ecommerce'].includes(this.accountType)
    },
    getActiveProjects(s: PublicPageData): IProject[] {
      return s.projectList.filter((project) => project.status === 'approved')
    },
    getActiveAndDeprecatedProjects(s: PublicPageData): IProject[] {
      return s.projectList.filter(
        (project) => project.status === 'approved' || project.status === 'deprecated',
      )
    },
    getSelectedProject(s: PublicPageData): IProject | undefined {
      if (!s.selectedProjectId) return undefined

      return s.projectList.find((project) => project.projectId === s.selectedProjectId)
    },
    getSelectedProjectEvidence(s: PublicPageData): IProjectEvidence | undefined {
      if (!s.selectedProjectId) return undefined

      return s.projectEvidenceById[s.selectedProjectId]
    },
    getSelectedProjectEvidenceLoading(s: PublicPageData): boolean {
      if (!s.selectedProjectId) return false

      return Boolean(s.isProjectEvidenceLoadingById[s.selectedProjectId])
    },
    getSelectedProjectPlantingGeoJSON(): FeatureCollection[] | null {
      const evidence = this.getSelectedProjectEvidence
      if (!evidence) return null

      const collections = evidence.evidences.flatMap((item) =>
        item.type === 'PlantingSession'
          ? [
              ...(item.planting.geoJSON ?? []),
              ...(item.siteAssessment.geoJSON ?? []),
              ...(item.externalReports ?? []).flatMap((report) => report.geoJSON ?? []),
            ]
          : [],
      )

      return collections.length ? collections : null
    },
    getSelectedProjectPlasticCollectionGeoJSON(): FeatureCollection[] | null {
      const evidence = this.getSelectedProjectEvidence
      if (!evidence) return null

      const collections = evidence.evidences.flatMap((item) =>
        item.type === 'PlasticCollection' ? [item.geoJSON] : [],
      )

      return collections.length ? collections : null
    },
  },
  actions: {
    async fetchPublicData(id: string, lng?: LanguageType) {
      try {
        const { data } = await getPublicAccount(id, lng)
        this.$patch({ ...data })

        if (inIFrame()) {
          dispatchMessage(window.parent, { type: EVENT_TYPES.UPDATE_PUBLIC_STORE, payload: data })
        }
      } catch (e) {
        console.error(e)
      }
    },
    async fetchCommunityData(lng?: LanguageType) {
      try {
        const { data } = await getCommunityAccount(lng)
        this.$patch({ ...data })
      } catch (e) {
        console.error(e)
      }
    },
    async fetchProjects(params?: IProjectRequestParams) {
      try {
        const { data } = await getProjects({ ...params })
        this.projectList = data

        if (!data.length) {
          this.selectedProjectId = null
          return
        }

        if (
          !this.selectedProjectId ||
          !data.some((project) => project.projectId === this.selectedProjectId)
        ) {
          this.selectedProjectId = data[0].projectId
        }
      } catch (e) {
        console.error(e)
      }
    },
    async fetchProjectEvidence(projectId: string): Promise<IProjectEvidence | undefined> {
      if (!projectId) return undefined

      const cachedEvidence = this.projectEvidenceById[projectId]
      if (cachedEvidence) {
        return cachedEvidence
      }

      if (this.isProjectEvidenceLoadingById[projectId]) {
        return undefined
      }

      this.isProjectEvidenceLoadingById = {
        ...this.isProjectEvidenceLoadingById,
        [projectId]: true,
      }

      try {
        const { data } = await getProjectEvidences(projectId)
        this.projectEvidenceById = {
          ...this.projectEvidenceById,
          [projectId]: data,
        }

        return data
      } catch (e) {
        console.error(`Failed to load project evidences for project ${projectId}`, e)
        return undefined
      } finally {
        this.isProjectEvidenceLoadingById = {
          ...this.isProjectEvidenceLoadingById,
          [projectId]: false,
        }
      }
    },
    setSelectedProjectId(projectId: string | null) {
      this.selectedProjectId = projectId
    },
    async selectProject(projectId: string): Promise<IProjectEvidence | undefined> {
      this.setSelectedProjectId(projectId)

      return await this.fetchProjectEvidence(projectId)
    },
    async fetchReceipts() {
      try {
        const { data } = await getReceipts()
        this.receipts = data.sort((a, b) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime()
        })
      } catch (e) {
        console.error(e)
      }
    },
    async fetchPurchases() {
      try {
        const { data } = await getPurchases()
        this.purchases = data
      } catch (e) {
        console.error(e)
      }
    },
  },
})
